import { createJurisdictionHeaders } from '@/data';
import { URLS } from '@/data/config';
import { hasNoLimit } from '@/feature/betslip-sbk/utils/validators';
import { useUserStore } from '@/hooks/use-user-store';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';

import { userLimitsKeys } from './query-keys';
import { UserLimit, UserLimitConfirmation, UserLimitType, UserLimitsSchema } from './types';

type UseUserLimitsQueryOptions<TData> = Omit<
    UseQueryOptions<UserLimit[], unknown, TData, ReturnType<typeof userLimitsKeys.limits>>,
    'queryKey'
>;

/**
 * Fetches user limits from /auth/user-limits endpoint
 * @return {Promise<UserLimit[]>} - user limits array
 */
const getUserLimits = async (): Promise<UserLimit[]> => {
    const resp = await (
        await fetch(`${URLS.CHAMELON_API_URL}/auth/user-limits`, {
            method: 'GET',
            headers: createJurisdictionHeaders(),
        })
    ).json();
    return UserLimitsSchema.parse(resp.data);
};

/**
 * Custom hook for getting user limits
 * @param {Object} options - react-query options
 * @return {Object} - react-query query object
 */
export const useUserLimits = <TData = UserLimit[]>(options?: UseUserLimitsQueryOptions<TData>) => {
    const sessionExpired = useUserStore(state => state.actions.sessionHasExpired);
    return useQuery({
        queryKey: userLimitsKeys.limits(),
        queryFn: getUserLimits,
        enabled: !sessionExpired(),
        staleTime: 10 * 60 * 1000, // 10 minutes
        ...options,
    });
};

/**
 * Selector that calculates the remaining amount for each active limit of a specific type
 * Filters out limits with no limit set
 * @param {UserLimitType} types - limit types
 */
export const activeLimitsByTypeSelector = (types: UserLimitType[]) => (limits: UserLimit[]) =>
    limits.filter(limit => types.includes(limit.type)).filter(limit => !hasNoLimit(limit));

/**
 * Selector for getting the count of pending limits
 * @param {string} type - limit type
 */
export const pendingLimitCountSelector = (type: UserLimitType) => (limits: UserLimit[]) =>
    limits.filter(limit => limit.type === type && limit.limit_goes_live_at > 0).length;

/**
 * Selector for getting the count of active limits
 * @param {string} type - limit type
 */
export const activeLimitsSelector = (type: UserLimitType) => (limits: UserLimit[]) =>
    limits.filter(limit => limit.type === type);

/**
 * Selector for getting the count of pending limits
 * @param {string} type - limit type
 */
export const pendingLimitsSelector = (type: UserLimitType) => (limits: UserLimit[]) =>
    limits.filter(limit => limit.type === type && limit.limit_goes_live_at > 0);

/**
 * Selector for if there are pending limits
 * @param {string} type - limit type
 */
export const hasPendingLimitsSelector = (type: UserLimitType) => (limits: UserLimit[]) =>
    pendingLimitsSelector(type)(limits).length > 0;

/**
 * Selector to get the pending limits while filtering out the limits that are already in confirmation state
 * Limits in confirmation states will also be displayed in pending state, we want to filter them out so that they are not displayed twice
 * @param {string} type - limit type
 * @param {UserLimitConfirmation[]} confirmationLimits - array of confirmation limits
 */
export const displayedPendingLimitsSelector =
    (type: UserLimitType, confirmationLimits?: UserLimitConfirmation[]) => (limits: UserLimit[]) =>
        pendingLimitsSelector(type)(limits).filter(
            limit =>
                !confirmationLimits?.find(
                    cl =>
                        cl.type === limit.type &&
                        cl.duration === limit.duration &&
                        cl.limit_goes_live_at === limit.limit_goes_live_at
                )
        );

export const limitsByTypesSelector = (types: UserLimitType[]) => (limits: UserLimit[]) => {
    return limits.filter(limit => types.includes(limit.type));
};
